// ── episode.js ────────────────────────────────────────────────────────────────
// Single source of truth for the small bits of season/episode math that were
// previously copy-pasted (and quietly diverged on > vs >=) across UpNext,
// WeeklySchedule, ShowDetail, Calendar, RaceBlock, Challenges, etc.
//
// Progress objects are { season, episode }. "Aired"/"last" objects from TMDB use
// { season_number, episode_number }. Helpers below normalize both shapes.

const S = (o) => (o?.season ?? o?.season_number ?? 0)
const E = (o) => (o?.episode ?? o?.episode_number ?? 0)

// Compare two season/episode markers. >0 if a is further than b, <0 if behind,
// 0 if identical. Accepts progress-shape or TMDB-shape on either side.
export function compareProgress(a, b) {
  if (!a && !b) return 0
  if (!a) return -1
  if (!b) return 1
  const sa = S(a), sb = S(b)
  if (sa !== sb) return sa - sb
  return E(a) - E(b)
}

// Behind = there is at least one AIRED episode the user hasn't reached. Because
// progress.episode is NEXT-to-watch, the next-to-watch episode ITSELF is unwatched —
// so "behind" is the last aired marker AT OR BEYOND next-to-watch (>=), not strictly
// beyond. The old `> 0` missed the most common case of all: a brand-new episode that
// just aired and you haven't started (next === lastAired). It also disagreed with
// getTracking's own caughtUp and with UpNext's inline check (Jason 2026-06-29).
// Returns false when either marker is missing (callers only ask once started).
export function isBehind(progress, lastAired) {
  if (!progress?.season || !lastAired) return false
  return compareProgress(lastAired, progress) >= 0
}

// Caught up = nothing aired left to watch right now: next-to-watch is STRICTLY beyond
// the last aired episode. The exact complement of isBehind when both markers exist
// (and matches getTracking's caughtUp).
export function isCaughtUp(progress, lastAired) {
  if (!progress?.season || !lastAired) return false
  return !isBehind(progress, lastAired)
}

// Fresh-drop test — the canonical "a new episode is available to watch RIGHT NOW"
// signal, shared by the Home greeting, the Tonight lineup, etc. TMDB advances
// next_episode_to_air the instant an episode airs (moving today's episode into
// last_episode_to_air), so a `next_episode_to_air === today` check misses exactly the
// shows that dropped today. This reads last_episode_to_air instead: true when it aired
// today — or yesterday, covering late-night viewing and timezone skew — AND the user
// hasn't watched past it. Pass today/yesterday as YYYY-MM-DD (todayKey()/dayKey()).
export function hasFreshDrop(show, progress, today, yesterday) {
  const last = show?.last_episode_to_air
  if (!last?.air_date) return false
  if (last.air_date !== today && last.air_date !== yesterday) return false
  // progress is NEXT-to-watch, so strictly beyond the last-aired marker = already seen;
  // <= 0 (at or before it, or not started at all) means the fresh drop is unwatched.
  return compareProgress(progress, last) <= 0
}

// The ONE "is this show finished airing forever" test. Case-insensitive and covers
// the British 'Cancelled' — some TMDB rows serialize it double-l, and exact-string
// checks (ShowDetail used three) silently misfiled those into the caught_up lifecycle
// instead of the completion path. Every ended/canceled decision routes through here.
export function isEndedStatus(showStatus) {
  return /^(ended|canceled|cancelled)$/i.test(showStatus || '')
}

// DISPLAY badge for a network-wrapped show — 'Ended' / 'Cancelled', or null. Unlike
// isEndedStatus (which drives LIFECYCLE and must stay purely status-based — never gate
// completion/caught-up on this), this SUPPRESSES the badge for a show that wrapped
// RECENTLY: TMDB flags every finished limited series / miniseries "Ended" the instant its
// run ends, so a brand-new drop (e.g. The Hawk, 2026) would otherwise wear an "old & dead"
// ENDED ribbon. Only genuinely aged shows (last aired ~2+ years ago) get the badge, where
// it reads as "a complete classic" rather than "stale" (Jason 2026-07-18).
export function endedBadge(show, todayStr) {
  if (!isEndedStatus(show?.status)) return null
  const last = show?.last_episode_to_air?.air_date || show?.last_air_date || show?.first_air_date
  if (last && todayStr) {
    const ageDays = (Date.parse(todayStr) - Date.parse(last)) / 86400000
    if (Number.isFinite(ageDays) && ageDays >= 0 && ageDays < 730) return null   // wrapped within ~2y → fresh
  }
  return /cancel/i.test(show.status) ? 'Cancelled' : 'Ended'
}

// When a returning show is between seasons — you're caught up and no next episode is
// scheduled yet — tell the user when it's expected back instead of dead-ending on
// "not yet scheduled" (Gina, 2026-07-20). Prefers TMDB's seasons list (which often
// carries the next season's premiere date before individual episodes are scheduled),
// then falls back to the renewal status. Returns a short human string, or null when
// there's genuinely nothing to say (episode already scheduled, or ended/canceled).
export function nextSeasonReturn(show, todayStr) {
  if (!show) return null
  if (show.next_episode_to_air?.air_date) return null   // an episode IS scheduled, handled elsewhere
  if (isEndedStatus(show.status)) return null            // ended/canceled, not coming back
  const today = todayStr ? Date.parse(todayStr) : Date.now()
  const upcoming = (show.seasons || [])
    .filter(s => s && s.season_number > 0 && s.air_date)
    .map(s => ({ n: s.season_number, t: Date.parse(s.air_date), raw: s.air_date }))
    .filter(s => Number.isFinite(s.t) && s.t > today)
    .sort((a, b) => a.t - b.t)[0]
  if (upcoming) {
    const d = new Date(upcoming.raw + 'T12:00:00')
    if (Number.isNaN(d.getTime())) return null
    const soon = (upcoming.t - today) < 45 * 86400000
    return soon
      ? `Season ${upcoming.n} returns ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : `Season ${upcoming.n} expected ${d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
  }
  if (/returning/i.test(show.status || '')) return 'Renewed, return date not announced yet'
  return null
}

// Display predicate: should this show be presented as CAUGHT UP rather than actively
// watching? RE-DERIVES from air data every time (a stored `caught_up` is only a lagging
// cache — the reconcile never runs on friends' rows and lags a newly-aired episode on
// your own), so a show that just got a new episode self-heals to "Watching" on every
// surface at once. Only falls back to the stored flag when there is NO air data to
// derive from. 'rewatching'/'completed' and ended/canceled shows are never caught-up
// here — they resolve through their own paths. (effectiveLastAired is hoisted below.)
export function showCaughtUp(show, progress, status, todayStr) {
  if (status !== 'watching' && status !== 'caught_up') return false
  if (isEndedStatus(show?.status)) return false
  const effLast = effectiveLastAired(show, todayStr)
  if (!effLast) return status === 'caught_up'   // no air data to derive → trust the stored flag
  return isCaughtUp(progress, effLast)
}

// The ONE caught_up ↔ watching flip decision, shared by every mutation path (the
// useWatchlist boot reconcile, ShowDetail's on-load flips + marking moment, and
// saveProgress's inline reconcile). Returns the status this row SHOULD hold, or null
// for "no change". Requires positive air data (effLast) in BOTH directions so a data
// gap can never thrash the status. Ended/canceled and non-watching/caught_up rows
// return null (they belong to the completion / rewatch paths, not here). Always pass
// todayStr so premiere-night ("airs today counts as aired") resolves correctly.
// Guard for caughtUpFlip's callers (ShowDetail's reconcile effect): a manual status pick
// (the status sheet) or an explicit action (RevivalBanner's Resume) writes the SAME
// wl.setStatus the reconcile effect reacts to, so the effect can't otherwise tell "the
// user just chose this" from "this is stale data from a fresh page load" — it would
// re-derive off the same air data and instantly flip the choice right back (Jason
// 2026-08-02: picking Watching for Lioness's new season reverted to Caught Up before he'd
// even seen it land). Signature-scoped to season+episode so it self-clears the moment
// progress actually changes (mark an episode, etc.), instead of pinning a choice forever.
// Lives here, not in a component, because both ShowDetail's status sheet and
// RevivalBanner's Resume button need the exact same key format.
export function setManualStatusOverride(safeStorage, showId, status, progress) {
  safeStorage.set(`nextep_status_override_${showId}`, JSON.stringify({
    status, season: progress?.season, episode: progress?.episode,
  }))
}

export function consumeManualStatusOverride(safeStorage, showId, status, progress) {
  const key = `nextep_status_override_${showId}`
  let override = null
  try { override = JSON.parse(safeStorage.get(key) || 'null') } catch { /* ignore */ }
  if (!override) return false
  const matches = override.status === status && override.season === progress?.season && override.episode === progress?.episode
  if (!matches) safeStorage.remove(key)   // stale — progress moved on since the pick, let auto-derive resume
  return matches
}

export function caughtUpFlip(show, progress, status, todayStr) {
  if (status !== 'watching' && status !== 'caught_up') return null
  if (isEndedStatus(show?.status)) return null
  const effLast = effectiveLastAired(show, todayStr)
  if (!effLast || !progress?.season) return null
  const caught = isCaughtUp(progress, effLast)
  if (status === 'watching'  &&  caught) return 'caught_up'
  if (status === 'caught_up' && !caught) return 'watching'
  return null
}

// Air-data-driven COMPLETION test — the promotion caughtUpFlip deliberately can't make.
// caughtUpFlip bails on ended shows (they belong to the completion path), which left a
// gap: a still-RETURNING show you're caught up on can later flip to Ended on TMDB (this
// routinely happens weeks after a finale airs), and if that last-aired ep you already
// watched turns out to BE the finale, nothing ever marks you done — the row stays stuck
// 'caught_up' forever (no Final Cut, no card, no completed_at, a "Caught Up ✨" pill on a
// show that has definitively ended). This returns true exactly for that case: a
// watching/caught_up row whose show is ended/canceled AND whose next-to-watch is STRICTLY
// beyond the finale (isCaughtUp against the ended show's last-aired = its finale). Requires
// positive air data so a data gap can never wrongly complete a show. Callers route a true
// here through the normal completion path (setStatus 'completed', quietly) so the card
// mints and completed_at stamps without a cinematic for a show finished long ago.
export function endedAndFinished(show, progress, status, todayStr) {
  if (status !== 'watching' && status !== 'caught_up') return false
  if (!isEndedStatus(show?.status)) return false
  if (!progress?.season) return false
  const effLast = effectiveLastAired(show, todayStr)
  if (!effLast) return false
  return isCaughtUp(progress, effLast)
}

// Real seasons only — drops season 0 (specials/pilots) which every call site
// was filtering by hand.
export function getValidSeasons(seasons) {
  return (seasons || []).filter(s => s && s.season_number > 0)
}

// Total episode count across real seasons.
export function totalEpisodes(seasons) {
  return getValidSeasons(seasons).reduce((sum, s) => sum + (s.episode_count || 0), 0)
}

// Estimated whole hours of TV across a set of shows: each show's episode count ×
// its runtime (40 min when TMDB omits it). The single source of truth for the
// "hours watched" / "days of TV" figure so Stats, Profile, and Timeline never
// disagree. Pass the shows you consider watched (typically completed) — this does
// NOT filter by status, so callers control scope.
export function estimateHours(shows) {
  const mins = (shows || []).reduce(
    (sum, s) => sum + ((s.episode_run_time?.[0] || 40) * (s.number_of_episodes || 0)), 0)
  return Math.round(mins / 60)
}

// estimateWatchedHours — total time you've ACTUALLY watched, across every tracked show,
// not just fully-completed ones. estimateHours(completed-only) undercounted badly and,
// worse, DROPPED when a completed show got a new season and flipped to caught_up/watching
// (its hours fell off the total). This counts watchedEpisodes per show (status-aware:
// completed = all, caught_up/watching = watched-through, via seasons) × runtime, so a
// caught-up 400-episode long-runner counts and the number only ever grows (Jason 2026-07-21).
export function estimateWatchedHours(shows, progressMap = {}, statusMap = {}) {
  const mins = (shows || []).reduce((sum, s) => {
    const st = statusMap[s.id]
    if (!st || st === 'plan_to_watch') return sum
    const rt  = s.episode_run_time?.[0] || 40
    const eps = watchedEpisodes(progressMap[s.id], s.seasons, st)
    return sum + eps * rt
  }, 0)
  return Math.round(mins / 60)
}

// Cumulative episodes watched given a { season, episode } progress and the show's
// seasons array. Counts every episode in fully-watched seasons plus the partial
// current season. This is the canonical version of the math RaceBlock/ShowDetail
// each had inline.
export function countWatchedEpisodes(progress, seasons) {
  if (!progress?.season) return 0
  return getValidSeasons(seasons).reduce((sum, s) => {
    if (s.season_number < progress.season) return sum + (s.episode_count || 0)
    if (s.season_number === progress.season) return sum + Math.min(progress.episode || 0, s.episode_count || 0)
    return sum
  }, 0)
}

// ── NEXT-to-watch progress math ─────────────────────────────────────────────
// IMPORTANT: progress.episode is the NEXT episode to watch (lastWatched + 1), so
// the watched count for the CURRENT season is episode-1, not episode. Surfaces
// that read it raw over-count by one (the MyList/UpNext/ShowDetail bug class,
// 2026-06-29). countWatchedEpisodes above takes a watched-THROUGH marker (callers
// pre-subtract); these take next-to-watch progress + status directly so every bar
// computes the same number. Use these for any "% watched" / "N of M" display.

// Episodes watched within ONE season given next-to-watch progress.
export function watchedInSeason(progress, season, status) {
  const count = season?.episode_count || 0
  if (status === 'completed') return count
  const ps = progress?.season || 0
  if (ps > season.season_number) return count                                  // a past, fully-watched season
  if (ps === season.season_number) return Math.min(Math.max(0, (progress.episode || 1) - 1), count)
  return 0                                                                      // not reached yet
}

// Cumulative episodes watched across all real seasons (next-to-watch aware).
export function watchedEpisodes(progress, seasons, status) {
  if (status !== 'completed' && !progress?.season) return 0
  return getValidSeasons(seasons).reduce((sum, s) => sum + watchedInSeason(progress, s, status), 0)
}

// Rounds a watched/total ratio for display but never claims "100%" unless it
// truly is complete: plain Math.round((711/714)*100) = 100 even with 3 episodes
// left, which read as a broken/lying progress bar (Jason 2026-08-15, Home's Canon
// card — "711 of 714 episodes · 100%"). Clamp a not-actually-done total to 99, and
// symmetrically never claim "0%" once ANY episode is watched (the same rounding
// can drop a tiny nonzero fraction to 0, which reads as "no progress").
export function roundPct(watched, total) {
  if (!total) return 0
  const pct = Math.round((watched / total) * 100)
  if (watched >= total) return 100
  if (pct >= 100) return 99
  if (pct <= 0 && watched > 0) return 1
  return pct
}

// Progress percent, clamped 0..100. scope 'series' (default) spans all seasons;
// scope 'season' is just the current season. The single source of truth for every
// progress bar.
export function progressPct(progress, seasons, status, scope = 'series') {
  if (scope === 'season') {
    const s = getValidSeasons(seasons).find(x => x.season_number === (progress?.season || 1))
    if (!s || !s.episode_count) return status === 'completed' ? 100 : 0
    return roundPct(watchedInSeason(progress, s, status), s.episode_count)
  }
  const total = totalEpisodes(seasons)
  if (!total) return status === 'completed' ? 100 : 0
  return roundPct(watchedEpisodes(progress, seasons, status), total)
}

// ── getTracking ─────────────────────────────────────────────────────────────
// THE SPINE. One selector that resolves everything a surface needs to know about
// a show's tracking state, so nothing recomputes "what's next" / "% watched" on
// its own (the divergence that caused this app's whole off-by-one bug class).
// `progress` is the stored next-to-watch marker { season, episode }. Pass
// todayStr (todayKey()) so this stays pure.
//
// Returns:
//   hasProgress     — user has started the show
//   next            — { season, episode } next-to-watch marker (raw; may point one
//                     past the last aired ep when caught up — check `caughtUp`)
//   lastWatched     — { season, episode } or null
//   watchedCount/totalCount, seasonPct/seriesPct
//   caughtUp        — nothing left to watch right now (or completed)
//   isSeasonFinale  — `next` is the finale of its season
//   isSeriesFinale  — `next` is the finale of the last season
//   airedThrough    — effectiveLastAired marker (last aired ep)
//   completed       — status === 'completed'
// `knownAired` (optional): the latest aired episode a caller has actually SEEN in the
// season's episode list, { season, episode }. On a batch-drop day TMDB's markers lag
// the list: Dead-End Job (2026-10-30) had last_episode_to_air null and
// next_episode_to_air = S1·E1 while all eight episodes listed today's date, so one mark
// made the show "All watched". The list is the better witness; it wins when newer.
export function getTracking(show, progress, status, todayStr, knownAired = null) {
  const seasons = getValidSeasons(show?.seasons)
  const completed = status === 'completed'
  const hasProgress = !!progress?.season
  const ns = progress?.season || (hasProgress ? 1 : null)
  const ne = progress?.episode || (hasProgress ? 1 : null)
  const next = (ns && ne) ? { season: ns, episode: ne } : null

  // Last watched = one step back from next-to-watch, season-aware.
  let lastWatched = null
  if (next) {
    if (next.episode > 1) lastWatched = { season: next.season, episode: next.episode - 1 }
    else {
      const prev = seasons.find(s => s.season_number === next.season - 1)
      lastWatched = prev ? { season: prev.season_number, episode: prev.episode_count || 1 } : null
    }
  }

  let airedThrough = effectiveLastAired(show, todayStr)
  if (knownAired?.season != null && knownAired?.episode != null && (!airedThrough || isBehindMarker(airedThrough, knownAired))) airedThrough = knownAired
  const caughtUp = completed || (!!airedThrough && !!next && (
    next.season > airedThrough.season ||
    (next.season === airedThrough.season && next.episode > airedThrough.episode)
  ))

  const curSeason  = seasons.find(s => s.season_number === ns)
  const lastSeason = seasons.length ? Math.max(...seasons.map(s => s.season_number)) : 1
  const isSeasonFinale = !!(next && curSeason?.episode_count && next.episode >= curSeason.episode_count)
  const isSeriesFinale = isSeasonFinale && ns === lastSeason

  // Season done (Jason 2026-10-18, Is It Cake? / Wonka): caught up, the marker sits one
  // past the season's last episode, and nothing is scheduled — "S2·E4 next" named an
  // episode that does not exist. Callers show the true state instead (see nextLabel).
  const scheduled = show?.next_episode_to_air?.air_date || null
  // Two shapes of "done": the marker sits one past the current season's last episode,
  // OR it has already rolled onto E1 of a season that is only a placeholder (Carrie,
  // 2026-11-01: S2 listed with 0 episodes and no date, so "S2 · E1 next" topped Waiting).
  const pastSeason = !!curSeason?.episode_count && next?.episode > curSeason.episode_count
  const onPlaceholder = !!next && next.episode === 1 && next.season > 1 && !(curSeason?.episode_count > 0) && !curSeason?.air_date
  const seasonDone = !completed && caughtUp && !!next && !scheduled && (pastSeason || onPlaceholder)

  return {
    hasProgress, next, lastWatched, completed, caughtUp, seasonDone, scheduled, nextSeasonListed: !!curSeason,
    watchedCount: watchedEpisodes(progress, seasons, status),
    totalCount:   totalEpisodes(seasons),
    seasonPct:    progressPct(progress, seasons, status, 'season'),
    seriesPct:    progressPct(progress, seasons, status, 'series'),
    isSeasonFinale, isSeriesFinale, airedThrough,
  }
}

// The one line under a show's title: what is actually next. Never names an episode
// that doesn't exist; says when the next one airs if it's known.
export function nextLabel(tr, todayStr) {
  if (!tr?.next) return 'Start from the beginning'
  if (tr.completed) return 'All watched'
  // "Season finished" was a guess: TMDB lists the next episode of a weekly show about a
  // week ahead, so between episodes the list simply ends. Name what is true instead.
  if (tr.seasonDone) {
    const through = tr.lastWatched || { season: tr.next.season, episode: tr.next.episode - 1 }
    const placeholder = tr.next.episode === 1 && tr.next.season > through.season && tr.nextSeasonListed
    return `Caught up through S${through.season} · E${through.episode} · ${placeholder ? `S${tr.next.season} not scheduled yet` : 'nothing scheduled yet'}`
  }
  if (tr.caughtUp && tr.scheduled) {
    const d = new Date(tr.scheduled + 'T12:00:00'), t = todayStr ? new Date(todayStr + 'T12:00:00') : null
    const days = t ? Math.round((d - t) / 86400000) : null
    const when = days === 0 ? 'tonight' : days === 1 ? 'tomorrow' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    return `S${tr.next.season} · E${tr.next.episode} airs ${when}`
  }
  return `S${tr.next.season} · E${tr.next.episode} next`
}

// Pretty label for a progress marker, e.g. "S2·E5" or "Not started".
export function fmtProgress(p) {
  return p?.season ? `S${p.season}·E${p.episode || 0}` : 'Not started'
}

// The canonical "what comes after watching this episode" step, honoring season
// boundaries. watched = { season, episode } just watched; returns the new
// next-to-watch marker: same-season +1 normally, S+1·E1 when the watched ep was
// the season finale and a next season exists. At the SERIES end (no next season)
// it deliberately overshoots to episode+1 within the final season — that is the
// established caught-up/finale idiom the rest of the app detects (progress
// strictly beyond last-aired = caught up; ShowDetail's finale detection keys on
// crossing the last episode). Falls back to a plain +1 when season data is
// missing, which matches the old behavior at every call site.
export function nextProgressAfter(watched, seasons) {
  const valid = getValidSeasons(seasons)
  const cur = valid.find(s => s.season_number === watched.season)
  if (cur && cur.episode_count && watched.episode >= cur.episode_count) {
    // The next EXISTING real season — not literally season+1, so a non-contiguous
    // numbering ([1,2,4], or a gap left by specials) still advances instead of stalling.
    const next = valid.filter(s => s.season_number > watched.season)
                      .sort((a, b) => a.season_number - b.season_number)[0]
    if (next) return { season: next.season_number, episode: 1 }
  }
  return { season: watched.season, episode: watched.episode + 1 }
}

// The FINAL episode of the WHOLE show — used to detect a series finale / complete a show.
// Prefers TMDB's last_episode_to_air (present for ended shows), then the highest real
// season's episode_count, then number_of_seasons/number_of_episodes. Distinct from
// effectiveLastAired: that's the last AIRED marker, which mid-run is NOT the finale.
// Shared by useWatchlist's auto-complete gate and ShowDetail's isConfirmedEndedFinale so
// an ended show with a null last_episode_to_air (TMDB gap) still auto-completes.
export function deriveFinalEpisode(show) {
  const last = show?.last_episode_to_air
  if (last?.season_number && last?.episode_number) {
    return { season: last.season_number, episode: last.episode_number }
  }
  const seasons = getValidSeasons(show?.seasons)
  if (seasons.length) {
    const finalSeason = seasons.reduce((a, b) => (a.season_number > b.season_number ? a : b))
    if (finalSeason.episode_count > 0) return { season: finalSeason.season_number, episode: finalSeason.episode_count }
  }
  if (show?.number_of_seasons && show?.number_of_episodes) {
    return { season: show.number_of_seasons, episode: show.number_of_episodes }
  }
  return null
}

// ── nextEpisodeAfter ──────────────────────────────────────────────────────────
// Given the FURTHEST episode a viewer has watched (season/episode), return the
// NEXT episode to watch as { season, episode }, or null if they've watched
// everything the show currently lists (finished / caught up). Powers the importer:
// a half-watched show is restored to its exact spot instead of blanket-completed.
// Uses the real-season episode counts; when the season map is missing, best-effort
// assumes another episode follows in the same season.
export function nextEpisodeAfter(show, season, episode) {
  const s = Number(season), e = Number(episode)
  if (!Number.isFinite(s) || !Number.isFinite(e)) return null
  const seasons = getValidSeasons(show?.seasons)
  if (!seasons.length) return { season: s, episode: e + 1 }
  const ordered = [...seasons].sort((a, b) => a.season_number - b.season_number)
  const cur = ordered.find(x => x.season_number === s)
  if (cur && e < cur.episode_count) return { season: s, episode: e + 1 }
  const nextSeason = ordered.find(x => x.season_number > s)
  if (nextSeason) return { season: nextSeason.season_number, episode: 1 }
  return null
}

// ── positionFitsShow ──────────────────────────────────────────────────────────
// Does a (season, episode) pair borrowed from ANOTHER database actually fit this
// TMDB show's structure? TV Time's export carries TVDB numbering, and the two
// databases disagree about split/combined episodes, alternate and DVD orders, and
// anime (absolute vs per-season numbering). So an imported "furthest watched" can
// name an episode TMDB has never heard of — S1E137 of a show whose seasons run to
// 26, or S3E24 where TMDB's season 3 stops at 22.
//
// This matters because nextEpisodeAfter() returns null for BOTH "they finished the
// series" and "these numbers don't fit", and the importer used to read null as
// finished: an ended show got marked completed and left the tracking list while the
// viewer was still mid-season, invisibly (reported on r/TVTime 2026-08-27).
// A false position must never be allowed to complete a show.
//
// Returns true when there is nothing to contradict (no season map), false when the
// season is absent from TMDB or the episode runs past that season's count.
// The real fix is resolving the TVDB episode id in show_seen_episode_latest.csv
// through /find, which would remove the guess entirely; this is the guard until then.
export function positionFitsShow(show, season, episode) {
  const s = Number(season), e = Number(episode)
  if (!Number.isFinite(s) || !Number.isFinite(e)) return false
  const seasons = getValidSeasons(show?.seasons)
  if (!seasons.length) return true
  const cur = seasons.find(x => x.season_number === s)
  if (!cur) return false
  if (!cur.episode_count) return true
  return e <= cur.episode_count
}

// ── effectiveLastAired ────────────────────────────────────────────────────────
// TMDB's last_episode_to_air is NULL for a show whose premiere hasn't aired —
// and every "aired" guard that does `show.last_episode_to_air ? guard : false`
// silently turns OFF for exactly those shows (2026-06-10: a premiere-night show
// allowed unbounded quick-mark advances, hid MyList's clapboard, and broke the
// episode-list unaired logic all at once). This derives a usable marker:
//   • last_episode_to_air when present (normal case)
//   • else from next_episode_to_air: if the next ep airs today or earlier it
//     counts as aired (premiere night — people watch live); otherwise
//     everything BEFORE it has aired (episode 0 sentinel = nothing yet)
//   • null only when the show has neither marker (callers keep legacy behavior)
// Returns { season, episode } or null. Pass todayStr (todayKey()) from the
// caller so this stays a pure function.
// Community quality for a TMDB episode object → { vote, count, t (0..1 tint), standout }
// or null when there aren't enough votes to mean anything. t maps 5.0 → 0 and
// 9.0 → 1: below a 5 nothing lights, and the scale's useful range is the top half.
// Standout = 8.5+. Same 10-vote floor for both (a 20-vote bar hid whole seasons
// of well-rated, lightly-voted shows — Jason 2026-09-20, Lioness). Used by the
// show page's ring tint and the Home deck's standout line.
export function episodeQuality(e) {
  const vote = Number(e?.vote_average) || 0, count = Number(e?.vote_count) || 0
  if (count < 10 || vote <= 0) return null
  const t = Math.max(0, Math.min(1, (vote - 5) / 4))
  return { vote: Math.round(vote * 10) / 10, count, t, standout: vote >= 8.5 }
}

const isBehindMarker = (a, b) => a.season < b.season || (a.season === b.season && a.episode < b.episode)

// The latest aired episode in one season's TMDB episode list → { season, episode } | null.
// Feed it to getTracking as `knownAired`.
export function lastAiredIn(episodes, season, todayStr) {
  let best = null
  for (const e of episodes || []) {
    if (!e?.air_date || e.air_date > todayStr) continue
    if (!best || e.episode_number > best.episode) best = { season, episode: e.episode_number }
  }
  return best
}

export function effectiveLastAired(show, todayStr) {
  // Specials (season 0) are not a position on the run: every other helper drops S0,
  // and a holiday special listed as last_episode_to_air would otherwise make the real
  // aired episodes unmarkable (isBehind({3,5},{0,x}) is false) and flip the row caught up.
  const real = (e) => (e && e.season_number === 0 ? null : e)
  const last = real(show?.last_episode_to_air)
  const next = real(show?.next_episode_to_air)
  // If next_episode_to_air has already aired (by today) AND is numerically past
  // whatever last_episode_to_air says, last_episode_to_air is stale — a season
  // that just premiered hasn't been re-synced into it yet. Trust the already-aired
  // next episode instead (Jason 2026-09-20, live: MobLand's S2E1 aired, but
  // last_episode_to_air still pointed at S1's finale, so the show read as
  // "caught up" the moment progress reached the real next episode and vanished
  // from Home's deck — showCaughtUp compared progress against the wrong marker).
  if (next?.season_number != null && next?.episode_number != null && todayStr && next.air_date && next.air_date <= todayStr) {
    const nextIsNewer = !last?.season_number || next.season_number > last.season_number ||
      (next.season_number === last.season_number && next.episode_number > last.episode_number)
    if (nextIsNewer) {
      // Batch-drop day on a show that is already over (Dead-End Job, 2026-10-30: status
      // Ended, last_episode_to_air null, next_episode_to_air = S1·E1 dated today, eight
      // episodes listed for today). An ended show has no future "next"; a "next" that has
      // aired means TMDB's markers haven't caught up with the drop, and the season it
      // points at is complete. Trust the season's episode count over the lagging marker.
      const season = (show?.seasons || []).find(s => s.season_number === next.season_number)
      if (isEndedStatus(show?.status) && season?.episode_count > next.episode_number) return { season: next.season_number, episode: season.episode_count }
      return { season: next.season_number, episode: next.episode_number }
    }
  }
  if (last?.season_number != null && last?.episode_number != null) {
    return { season: last.season_number, episode: last.episode_number }
  }
  if (next?.season_number != null && next?.episode_number != null) {
    // Already-aired case is handled above; reaching here means it hasn't aired yet.
    if (next.episode_number > 1) {
      return { season: next.season_number, episode: next.episode_number - 1 }
    }
    if (next.season_number === 1) return { season: 1, episode: 0 } // nothing aired yet
    const prevSeason = (show?.seasons || []).find(s => s.season_number === next.season_number - 1)
    return { season: next.season_number - 1, episode: prevSeason?.episode_count || 0 }
  }
  return null
}

// ── airedSeasonCount ─────────────────────────────────────────────────────────
// How many seasons a viewer can ACTUALLY watch today.
//
// `number_of_seasons` from TMDB counts ANNOUNCED seasons, which is a trap this codebase
// has now hit twice: EpisodeMultiPicker knew about it and worked around it locally, while
// returningShows didn't and told Jason "1 new season since you finished" about Severance
// season 3 — air_date null, episode_count 0 (2026-07-31). Reach for THIS rather than
// subtracting number_of_seasons again.
//
// Season 0 is specials and never counts.
// today as YYYY-MM-DD. Defaulted here rather than importing datetime, because this file
// is deliberately pure and node --test friendly.
export function airedSeasonCount(show, today = new Date().toISOString().slice(0, 10)) {
  const seasons = Array.isArray(show?.seasons) ? show.seasons : null
  if (!seasons) return Number(show?.number_of_seasons) || 0   // older cached blob
  return seasons.filter(s => {
    const n = Number(s?.season_number)
    return Number.isFinite(n) && n >= 1
      && Number(s?.episode_count) > 0
      && s?.air_date && String(s.air_date) <= today
  }).length
}

// ── Race fairness: the fresh-drop grace window ────────────────────────────────
// A race on a currently-airing show measured DISTRIBUTION, not watching: a
// live-TV viewer marks the new episode the night it airs, while a streaming
// viewer structurally can't until it reaches their service (Hulu next-day, etc).
// The "leader" pill then shames someone for their provider (Jason vs Melissa on
// Deadliest Catch, 2026-08-01).
//
// Rule: if the TRAILING racer's next-to-watch is exactly the latest-aired
// episode, and that episode aired within the last GRACE_HOURS, no leader is
// declared — the race shows a neutral "new episode — window open" state. A lead
// that survives the window is a real lead and shows normally. Applies in both
// directions (your fresh-drop lead over them is equally not-a-lead).
//
// progress.episode is NEXT-to-watch (the house footgun): trailing.next === the
// latest aired ep means they've seen everything EXCEPT the fresh drop.
export const RACE_GRACE_HOURS = 48

export function raceFreshDropGrace(show, myProg, oppProg, todayStr, nowMs = null) {
  const air = show?.last_episode_to_air?.air_date
  if (!air) return false
  const last = effectiveLastAired(show, todayStr)
  if (!last) return false
  const cmp = compareProgress(myProg, oppProg)
  if (cmp === 0) return false                       // dead heat needs no grace
  const trailing = cmp < 0 ? myProg : oppProg
  if (!trailing?.season) return false
  const trailingAtFresh =
    Number(trailing.season) === last.season && Number(trailing.episode) === last.episode
  if (!trailingAtFresh) return false                // behind by more than the fresh drop
  const aired = new Date(air + 'T00:00:00Z').getTime()
  const now = nowMs ?? Date.now()
  // Date-only air stamps have no timezone/hour; measure from end-of-air-day so a
  // same-night marker never expires the window early.
  return now - aired < (RACE_GRACE_HOURS + 24) * 3600 * 1000
}

// ── TVDB episode-id resolution (TV Time import) ───────────────────────────────
// TV Time ships TVDB numbering; TMDB disagrees on split/combined episodes,
// alternate orders and anime absolute-vs-per-season numbering. The export also
// carries the TVDB EPISODE id of the furthest-watched point, which TMDB's
// /find resolves to its own season/episode, sidestepping the arithmetic. These
// two helpers decide WHEN that round-trip is worth paying and WHAT of the answer
// to trust; the importer does the fetch.

// Shows where the two databases habitually disagree even when the numbers look
// in range: animation, and anything of Japanese/Korean origin (cours, absolute
// numbering, recap specials counted as episodes).
export function numberingSuspect(show) {
  const genres = Array.isArray(show?.genres) ? show.genres : []
  if (genres.some(g => g?.id === 16 || /animation/i.test(g?.name || ''))) return true
  const origin = Array.isArray(show?.origin_country) ? show.origin_country : []
  return origin.some(c => c === 'JP' || c === 'KR')
}

// Resolve when the position doesn't fit TMDB at all, or the show is a suspect —
// never on the common agreeing case, which should not pay a network call per show.
export function shouldResolveEpisodeId(show, fits, hasEpisodeId) {
  if (!hasEpisodeId) return false
  return !fits || numberingSuspect(show)
}

// The /find answer, trusted only when it is a real episode OF THIS SHOW. A TVDB
// episode id that resolves into another series (a wrong show match upstream, a
// stale id) must not move progress here.
export function episodeFromFind(findResult, showId) {
  const ep = findResult?.tv_episode_results?.[0]
  if (!ep) return null
  if (showId != null && ep.show_id != null && Number(ep.show_id) !== Number(showId)) return null
  const season = Number(ep.season_number), episode = Number(ep.episode_number)
  if (!(season >= 1) || !(episode >= 1)) return null
  return { season, episode }
}
